import { BELLOWS_FIRE_REST } from '../../models/bellows.ts';
import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inExpo, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';

/*
 * El Fuelle (models/bellows.ts): squat, heavy, it stomps. Its chest is the bellows: `lift` spreads the lid off
 * the lower plate over the three folds (each fold and the lid rise a quarter of it), so the bellows draw open
 * (lift > 0) or slam shut (lift < 0).
 *
 * The rule: 'inhale' (2.0 s, the sim's inhale): the bellows draw open (to +0.3 by 1.6 s, held), it rears back,
 * arms spread, the grate of its mouth swinging open. 'blow' (1.6 s): the bellows slam shut (0.12 s), it lurches
 * forward and the fire pours from its mouth (the `fire` bone, built tiny: on from 0.12 s to 1.2 s). 'attack' a
 * hammer blow overhead (lands at 0.52 s).
 *
 * Conventions: +X on `top` bows the lid and face forward; −X on `jaw` swings the grate and beard out (open);
 * −X on a leg or arm swings it forward and up, −X on a forearm bends the elbow; +Z on the right arm lifts it out.
 */

type B = 'root' | 'body' | 'legL' | 'legR' | 'fold1' | 'fold2' | 'fold3' | 'top' | 'jaw' | 'fire' | 'armL' | 'foreL' | 'armR' | 'foreR';
type P = KeyPose<B>;
const FIRE = 1 / BELLOWS_FIRE_REST;

/** Opens (lift > 0) or shuts (lift < 0) the bellows. */
const bellow = (b: Bones<B>, lift: number): void => {
  const q = lift / 4;
  b.fold1.pos(0, q, 0);
  b.fold2.pos(0, q, 0);
  b.fold3.pos(0, q, 0);
  b.top.pos(0, q, 0);
};

const idle = (): Clip<B> =>
  clip<B>('idle', 3, true, (b, t) => {
    const p = phase(t, 3) * TAU;
    bellow(b, 0.05 * (1 + Math.sin(p)));
    b.top.rot(-0.02 * Math.sin(p), 0.04 * Math.sin(p * 0.5 * 2), 0);
    b.jaw.rot(-0.05 * (1 + Math.sin(p)));
    b.armL.rot(0.03 * Math.sin(p), 0, -0.04 - 0.02 * Math.sin(p));
    b.armR.rot(0.03 * Math.sin(p + 1), 0, 0.04 + 0.02 * Math.sin(p));
    b.foreL.rot(-0.15);
    b.foreR.rot(-0.15);
  }, (t) => {
    const k = Math.sin(phase(t, 3) * TAU);
    return [1 + 0.01 * k, 1, 1 + 0.01 * k];
  });

const walk = (dur = 1.8): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th);
    const stomp = Math.max(0, Math.cos(2 * th));
    b.legL.rot(-0.35 * sn, 0, 0);
    b.legR.rot(0.35 * sn, 0, 0);
    b.root.pos(0, 0.04 * Math.abs(Math.cos(th)) - 0.03, 0).rot(0, 0.06 * sn, 0.07 * Math.cos(th));
    bellow(b, 0.04 * stomp - 0.02);
    b.top.rot(0.05, -0.08 * sn, -0.04 * Math.cos(th));
    b.armL.rot(0.3 * sn, 0, -0.06);
    b.armR.rot(-0.3 * sn, 0, 0.06);
    b.foreL.rot(-0.25);
    b.foreR.rot(-0.25);
    b.jaw.rot(-0.08 * stomp);
  }, (t) => {
    const k = Math.max(0, Math.cos(2 * phase(t, dur) * TAU));
    return [1 + 0.03 * k, 1 - 0.04 * k, 1 + 0.03 * k];
  });

const attack = (): Clip<B> =>
  clip<B>('attack', 1.4, false, (b, t) => {
    const up: P = { root: [0, -0.15, 0], top: [-0.15, -0.2, 0], armR: [-2.7, 0, 0.3], foreR: [-0.9, 0, 0], armL: [-0.3, 0, -0.3], foreL: [-0.4, 0, 0], legL: [-0.2, 0, 0], legR: [0.15, 0, 0] };
    const down: P = { root: [0, 0.1, 0, 0, -0.04, 0.12], top: [0.3, 0.15, 0], armR: [-0.7, 0, 0.1], foreR: [-0.1, 0, 0], armL: [0.2, 0, -0.35], foreL: [-0.3, 0, 0], legL: [-0.35, 0, 0], legR: [0.2, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.4, up, inOut],
      [0.52, down, inExpo],
      [0.8, { ...down, top: [0.26, 0.15, 0] }, outCubic],
      [1.4, {}, inOut],
    ], { foreR: 0.03 });
    bellow(b, 0.12 * span(t, 0, 0.4) * (1 - span(t, 0.45, 0.55)) - 0.05 * envelope(t, 0.5, 0.55, 0.8, 1.2));
  }, (t) => {
    const q = Math.max(0, settle(t - 0.52, 3, 8)) * (t > 0.52 ? 1 : 0);
    return [1 + 0.05 * q, 1 - 0.06 * q, 1 + 0.05 * q];
  });

const DRAWN: P = { root: [0, 0, 0, 0, 0, -0.05], top: [-0.22, 0, 0], jaw: [-0.45, 0, 0], armL: [0.35, 0, -0.55], foreL: [-0.5, 0, 0], armR: [0.35, 0, 0.55], foreR: [-0.5, 0, 0], legL: [-0.1, 0, 0], legR: [0.12, 0, 0] };

/** The rule, part one: the bellows draw open, it rears back with its arms spread and the grate opens. */
const inhale = (): Clip<B> =>
  clip<B>('inhale', 2.0, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [1.6, DRAWN, inOut],
      [2.0, { ...DRAWN, top: [-0.24, 0, 0] }],
    ]);
    bellow(b, 0.3 * span(t, 0, 1.6, inOut));
    // The draw shudders as it fills.
    b.top.rot(0.015 * Math.sin(t * 30) * span(t, 0.3, 1.6), 0, 0);
  }, (t) => {
    const k = span(t, 0, 1.6, inOut);
    return [1 + 0.05 * k, 1 + 0.02 * k, 1 + 0.05 * k];
  }, 'Inhala');

/** The rule, part two: the bellows slam shut and the fire pours out of its mouth in a cone. */
const blow = (): Clip<B> =>
  clip<B>('blow', 1.6, false, (b, t) => {
    const out: P = { root: [0, 0, 0, 0, -0.03, 0.12], top: [0.22, 0, 0], jaw: [-0.6, 0, 0], armL: [-0.5, 0, -0.45], foreL: [-0.3, 0, 0], armR: [-0.5, 0, 0.45], foreR: [-0.3, 0, 0], legL: [-0.35, 0, 0], legR: [0.25, 0, 0] };
    timeline(b, t, [
      [0, DRAWN],
      [0.12, out, outExpo],
      [1.2, { ...out, top: [0.25, 0, 0] }],
      [1.6, {}, inOut],
    ]);
    bellow(b, 0.3 - 0.36 * span(t, 0, 0.12, outExpo) + 0.06 * span(t, 1.2, 1.6, inOut));
    const on = span(t, 0.08, 0.2, outExpo) * (1 - span(t, 1.1, 1.25, inQuad));
    b.fire.scale(1 + (FIRE - 1) * Math.max(0, on) * (0.93 + 0.07 * Math.sin(t * 33)));
    b.top.rot(0.02 * Math.sin(t * 45) * on, 0, 0);
  }, (t) => {
    const q = Math.max(0, settle(t - 0.12, 3, 7)) * (t > 0.12 ? 1 : 0);
    return [1 + 0.05 - 0.05 * span(t, 0, 0.12) + 0.04 * q, 1 - 0.05 * q, 1 + 0.04 * q];
  }, 'Sopla');

const hit = (): Clip<B> =>
  clip<B>('hit', 0.5, false, (b, t) => {
    const q = settle(t, 2.4, 8) * span(t, 0, 0.04, outQuad);
    b.root.pos(0, 0, -0.04 * q);
    b.top.rot(-0.12 * q, 0.05 * q, 0.04 * q);
    b.jaw.rot(-0.25 * Math.max(0, q));
    b.armL.rot(-0.15 * q, 0, -0.1 * q);
    b.armR.rot(-0.15 * q, 0, 0.1 * q);
    bellow(b, -0.04 * q);
  });

const stun = (): Clip<B> =>
  clip<B>('stun', 2.6, true, (b, t) => {
    const p = phase(t, 2.6) * TAU;
    bellow(b, -0.04);
    b.root.rot(0, 0, 0.03 * Math.sin(p));
    b.top.rot(0.2 + 0.04 * Math.sin(p * 2), 0.15 * Math.sin(p), 0.06 * Math.cos(p));
    b.jaw.rot(-0.2);
    b.armL.rot(0.1, 0, 0.05);
    b.armR.rot(0.1, 0, -0.05);
  });

/** Death (2.6 s): a last gasp of the bellows, a sputter of smoke, then it topples onto its back with a crash. */
const die = (): Clip<B> =>
  clip<B>('die', 2.6, false, (b, t) => {
    const gasp: P = { top: [-0.3, 0.1, 0], jaw: [-0.6, 0, 0], armL: [-1.4, 0, -0.6], foreL: [-0.4, 0, 0], armR: [-1.3, 0, 0.7], foreR: [-0.4, 0, 0] };
    const down: P = { root: [-1.35, 0.1, 0.05, 0, 0.35, -0.3], top: [-0.2, 0.2, 0], jaw: [-0.35, 0, 0], armL: [-0.4, 0, -1.0], foreL: [-0.2, 0, 0], armR: [-0.5, 0, 1.1], foreR: [-0.2, 0, 0], legL: [-0.5, 0, 0], legR: [-0.3, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.5, gasp, outCubic],
      [1.0, { ...gasp, top: [-0.1, 0.1, 0] }, inOut],
      [1.9, down, inCubic],
      [2.15, { ...down, root: [-1.3, 0.1, 0.05, 0, 0.33, -0.3] }, outBack],
      [2.6, down, inOut],
    ], { foreL: 0.04, foreR: 0.04 });
    bellow(b, 0.15 * span(t, 0, 0.5) - 0.2 * span(t, 0.6, 1.0, inExpo) + 0.04 * span(t, 1.0, 1.9));
  }, (t) => {
    const q = Math.max(0, settle(t - 1.9, 2.5, 7)) * (t > 1.9 ? 1 : 0);
    return [1 + 0.05 * q, 1 - 0.06 * q, 1 + 0.05 * q];
  });

/** Clips by library kind (see ../rigs.ts). */
export const bellowsClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), inhale(), blow()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { bellows: bellowsClips };
